/**
 * SharedReflectionPage
 *
 * Public page for viewing a shared reflection via token URL.
 * Three-step flow: info screen -> email/PIN entry -> tabbed content view.
 * No authentication required.
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Lock,
  Mail,
  AlertCircle,
  FileText,
  Headphones,
  MessageSquare,
  Lightbulb,
  RefreshCw,
} from "lucide-react";
import Markdown from "react-markdown";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  getShareInfo,
  requestShareAccess,
  verifySharePin,
} from "@/services/shares";
import type { ShareInfoResponse, ShareContentResponse } from "@/types/share";

type Step = "loading" | "info" | "pin" | "content" | "error";

/** Format seconds to mm:ss */
function fmtTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function SharedReflectionPage() {
  const { token } = useParams<{ token: string }>();
  const [step, setStep] = useState<Step>("loading");
  const [info, setInfo] = useState<ShareInfoResponse | null>(null);
  const [content, setContent] = useState<ShareContentResponse | null>(null);
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const pinRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!token) return;
    getShareInfo(token)
      .then((data) => {
        setInfo(data);
        setStep("info");
      })
      .catch(() => {
        setStep("error");
      });
  }, [token]);

  const handleRequestAccess = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !email) return;
    setSubmitting(true);
    setError("");
    try {
      await requestShareAccess(token, email);
      setStep("pin");
      setTimeout(() => pinRef.current?.focus(), 100);
    } catch {
      setError("Failed to send access code. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyPin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !email || !pin) return;
    setSubmitting(true);
    setError("");
    try {
      const data = await verifySharePin(token, email, pin);
      setContent(data);
      setStep("content");
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 401) {
        setError("Incorrect code. Try again.");
        setPin("");
      } else if (status === 410) {
        setStep("error");
      } else {
        setError("Verification failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (step === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (step === "error") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-4">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto" />
          <h1 className="text-xl font-semibold text-foreground">
            Link Expired
          </h1>
          <p className="text-muted-foreground">
            This link has expired or is no longer valid.
          </p>
        </div>
      </div>
    );
  }

  if (step === "info" && info) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full space-y-6">
          <div className="text-center space-y-2">
            <Lock className="w-10 h-10 text-primary mx-auto" />
            <h1 className="text-xl font-semibold text-foreground">
              Shared Reflection
            </h1>
            {info.reflection_title && (
              <p className="text-muted-foreground font-medium">
                {info.reflection_title}
              </p>
            )}
          </div>

          <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
            <p className="text-muted-foreground">This share includes:</p>
            <div className="flex flex-wrap gap-2">
              {info.include_transcript && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-background rounded text-foreground text-xs">
                  <FileText className="w-3 h-3" /> Transcript
                </span>
              )}
              {info.include_audio && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-background rounded text-foreground text-xs">
                  <Headphones className="w-3 h-3" /> Audio
                </span>
              )}
              {info.include_feedback && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-background rounded text-foreground text-xs">
                  <Lightbulb className="w-3 h-3" /> Insights
                </span>
              )}
              {info.include_chat && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-background rounded text-foreground text-xs">
                  <MessageSquare className="w-3 h-3" /> Chat
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Expires{" "}
              {new Date(info.share_expires_at).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </p>
          </div>

          <form onSubmit={handleRequestAccess} className="space-y-3">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Enter your email to request access
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? "Sending..." : "Send Access Code"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (step === "pin") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full space-y-6">
          <div className="text-center space-y-2">
            <Lock className="w-10 h-10 text-primary mx-auto" />
            <h1 className="text-xl font-semibold text-foreground">
              Enter Access Code
            </h1>
            <p className="text-sm text-muted-foreground">
              We sent a 6-digit code to <strong>{email}</strong>
            </p>
          </div>

          <form onSubmit={handleVerifyPin} className="space-y-4">
            <input
              ref={pinRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={pin}
              onChange={(e) =>
                setPin(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              placeholder="000000"
              className="w-full text-center text-3xl tracking-[0.5em] font-mono py-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {error && (
              <p className="text-sm text-destructive text-center">{error}</p>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={pin.length !== 6 || submitting}
            >
              {submitting ? "Verifying..." : "Verify"}
            </Button>
          </form>

          <p className="text-xs text-center text-muted-foreground">
            Didn&apos;t receive a code?{" "}
            <button
              type="button"
              onClick={() => {
                setStep("info");
                setPin("");
                setError("");
              }}
              className="text-primary hover:underline"
            >
              Try again
            </button>
          </p>
        </div>
      </div>
    );
  }

  if (step === "content" && content) {
    return <SharedContentView content={content} />;
  }

  return null;
}

/**
 * Tabbed content viewer for shared reflection data.
 */
function SharedContentView({ content }: { content: ShareContentResponse }) {
  // Build available tabs based on what was included
  const tabs = useMemo(() => {
    const t: { id: string; label: string; icon: React.ReactNode }[] = [];
    if (content.include_transcript)
      t.push({
        id: "transcript",
        label: "Transcript",
        icon: <FileText className="w-4 h-4" />,
      });
    if (content.include_feedback)
      t.push({
        id: "feedback",
        label: "Insights",
        icon: <Lightbulb className="w-4 h-4" />,
      });
    if (content.include_chat)
      t.push({
        id: "chat",
        label: "Chat",
        icon: <MessageSquare className="w-4 h-4" />,
      });
    if (content.include_audio)
      t.push({
        id: "audio",
        label: "Audio",
        icon: <Headphones className="w-4 h-4" />,
      });
    return t;
  }, [content]);

  const defaultTab = tabs[0]?.id ?? "transcript";

  // Build a speaker lookup map: speaker_label -> custom_name / display_label
  const speakerMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (content.speakers) {
      for (const s of content.speakers) {
        const label = (s.speaker_label as string) ?? "";
        const name =
          (s.custom_name as string) ||
          (s.display_label as string) ||
          label ||
          "Speaker";
        if (label) map[label] = name;
      }
    }
    return map;
  }, [content.speakers]);

  return (
    <div className="min-h-screen bg-background">
      {/* CTA banner */}
      <div className="bg-primary/5 border-b border-primary/20 px-4 py-3 text-center text-sm">
        <span className="text-foreground">
          You&apos;re viewing a shared reflection.{" "}
        </span>
        <a
          href="/get-started"
          className="text-primary font-medium hover:underline"
        >
          Sign up for AttuneWell
        </a>{" "}
        <span className="text-muted-foreground">
          to analyse your own conversations.
        </span>
      </div>

      <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {content.reflection_title || "Shared Reflection"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {new Date(content.reflection_created_at).toLocaleDateString(
              undefined,
              { year: "numeric", month: "long", day: "numeric" }
            )}
          </p>
        </div>

        {/* Tabbed content */}
        {tabs.length > 0 && (
          <Tabs defaultValue={defaultTab} className="w-full">
            <TabsList className="w-full justify-start">
              {tabs.map((t) => (
                <TabsTrigger
                  key={t.id}
                  value={t.id}
                  className="gap-1.5 text-sm"
                >
                  {t.icon}
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Transcript tab */}
            {content.include_transcript && (
              <TabsContent value="transcript" className="mt-4">
                {content.transcript_segments &&
                content.transcript_segments.length > 0 ? (
                  <div className="space-y-0">
                    {content.transcript_segments.map((seg, i) => {
                      const label =
                        (seg.speaker_label as string) ??
                        (seg.speaker as string) ??
                        "";
                      const displayName =
                        speakerMap[label] || label || `Speaker ${i + 1}`;
                      const text = (seg.text as string) ?? "";
                      const startTime = seg.start_time as number | undefined;
                      return (
                        <div
                          key={i}
                          className={`p-3 ${i % 2 === 0 ? "bg-muted/20" : "bg-muted/40"}`}
                        >
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="text-xs font-semibold text-primary">
                              {displayName}
                            </span>
                            {startTime != null && (
                              <span className="text-xs text-muted-foreground">
                                {fmtTime(startTime)}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-foreground">{text}</p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    Transcript not available.
                  </p>
                )}
              </TabsContent>
            )}

            {/* Feedback / Insights tab */}
            {content.include_feedback && (
              <TabsContent value="feedback" className="mt-4 space-y-6">
                {!content.feedback && !content.insights ? (
                  <p className="text-sm text-muted-foreground italic">
                    Analysis not yet available for this reflection.
                  </p>
                ) : (
                  <>
                    {content.feedback && (
                      <div className="prose prose-sm max-w-none text-foreground bg-muted/30 rounded-lg p-4">
                        <Markdown>
                          {
                            (content.feedback as Record<string, unknown>)
                              .feedback_text as string
                          }
                        </Markdown>
                      </div>
                    )}
                    {content.insights &&
                      (
                        content.insights as {
                          key_themes?: {
                            title: string;
                            description: string;
                          }[];
                        }
                      ).key_themes &&
                      (
                        content.insights as {
                          key_themes: {
                            title: string;
                            description: string;
                          }[];
                        }
                      ).key_themes.length > 0 && (
                        <div className="space-y-3">
                          <h3 className="text-sm font-semibold text-foreground">
                            Key Themes
                          </h3>
                          <div className="grid gap-3 sm:grid-cols-2">
                            {(
                              content.insights as {
                                key_themes: {
                                  title: string;
                                  description: string;
                                }[];
                              }
                            ).key_themes.map((theme, i) => (
                              <div
                                key={i}
                                className="rounded-lg border border-border p-3"
                              >
                                <p className="text-sm font-medium text-foreground">
                                  {theme.title}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {theme.description}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    {content.insights &&
                      (
                        content.insights as {
                          session_summary?: string;
                        }
                      ).session_summary && (
                        <div className="bg-muted/30 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-foreground mb-2">
                            Session Summary
                          </h3>
                          <p className="text-sm text-foreground">
                            {
                              (
                                content.insights as {
                                  session_summary: string;
                                }
                              ).session_summary
                            }
                          </p>
                        </div>
                      )}
                  </>
                )}
              </TabsContent>
            )}

            {/* Chat tab */}
            {content.include_chat && (
              <TabsContent value="chat" className="mt-4">
                {!content.chat_messages ||
                content.chat_messages.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    No chat history available for this reflection.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {content.chat_messages.map((msg, i) => {
                      const m = msg as {
                        role: string;
                        content: string;
                        created_at?: string;
                      };
                      const isAssistant = m.role === "assistant";
                      return (
                        <div
                          key={i}
                          className={`flex ${isAssistant ? "justify-start" : "justify-end"}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-lg p-3 text-sm ${
                              isAssistant
                                ? "bg-primary/5 text-foreground"
                                : "bg-muted text-foreground"
                            }`}
                          >
                            <p className="text-xs font-medium mb-1 text-muted-foreground">
                              {isAssistant ? "Therapist" : "You"}
                            </p>
                            <p className="whitespace-pre-wrap">{m.content}</p>
                            {m.created_at && (
                              <p className="text-xs text-muted-foreground mt-1 text-right">
                                {new Date(m.created_at).toLocaleTimeString(
                                  undefined,
                                  {
                                    hour: "numeric",
                                    minute: "2-digit",
                                  }
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            )}

            {/* Audio tab */}
            {content.include_audio && (
              <TabsContent value="audio" className="mt-4 space-y-3">
                {content.audio_url ? (
                  <>
                    <audio
                      controls
                      src={content.audio_url}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <RefreshCw className="w-3 h-3" />
                      Audio link expires in 5 minutes. Reload the page to
                      refresh.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    Audio not available.
                  </p>
                )}
              </TabsContent>
            )}
          </Tabs>
        )}

        {/* CTA footer */}
        <div className="border-t border-border pt-6 text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            Want to record and analyse your own therapy sessions?
          </p>
          <a href="/get-started">
            <Button>Get started with AttuneWell</Button>
          </a>
        </div>
      </div>
    </div>
  );
}
